/**
 * Parses a stringified file list from API response back to proper array format
 * @param {string|array} fileListString - The stringified file list from API
 * @returns {array} - Parsed array of file objects
 */
export const parseFileListFromString = (fileListString) => {
    if (!fileListString || fileListString === '') return [];

    try {
        // If it's already an array, return it
        if (Array.isArray(fileListString)) {
            return fileListString;
        }

        // If it's a string that looks like an array
        if (typeof fileListString === 'string' && fileListString.trim().startsWith('[')) {
            // Remove the outer brackets and split by objects
            const cleanString = fileListString.trim().slice(1, -1); // Remove [ and ]

            // Split by } { pattern to separate multiple objects
            const objectStrings = cleanString.split(/}\s*,\s*{/);

            const parsedFiles = objectStrings.map((objStr, index) => {
                // Add back the braces if they were removed during split
                let objectString = objStr.trim();
                if (index > 0 && !objectString.startsWith('{')) {
                    objectString = '{' + objectString;
                }
                if (index < objectStrings.length - 1 && !objectString.endsWith('}')) {
                    objectString = objectString + '}';
                }
                if (!objectString.startsWith('{')) {
                    objectString = '{' + objectString;
                }
                if (!objectString.endsWith('}')) {
                    objectString = objectString + '}';
                }

                // Extract values using regex patterns
                const urlMatch = objectString.match(/url:\s*([^,\n\r}]+)/);
                const nameMatch = objectString.match(/name:\s*([^,\n\r}]+)/);
                const sizeMatch = objectString.match(/size:\s*(\d+)/);
                const typeMatch = objectString.match(/type:\s*([^,\n\r}]+)/);
                const lastModifiedMatch = objectString.match(/lastModified:\s*(\d+)/);
                const webkitRelativePathMatch = objectString.match(/webkitRelativePath:\s*([^,\n\r}]*)/);

                return {
                    url: urlMatch ? urlMatch[1].trim() : '',
                    name: nameMatch ? nameMatch[1].trim() : 'unknown',
                    size: sizeMatch ? parseInt(sizeMatch[1]) : 0,
                    type: typeMatch ? typeMatch[1].trim() : 'application/octet-stream',
                    lastModified: lastModifiedMatch ? parseInt(lastModifiedMatch[1]) : Date.now(),
                    webkitRelativePath: webkitRelativePathMatch ? webkitRelativePathMatch[1].trim() : ''
                };
            });

            return parsedFiles.filter(file => file.url); // Only return files with valid URLs
        }

        return [];
    } catch (error) {
        console.error('Error parsing file list string:', error);
        return [];
    }
};

/**
 * Processes API response data to parse all file list fields
 * @param {array} apiData - Array of data from API response
 * @param {array} fileFields - Array of field names that contain file lists (e.g., ['thumbnail', 'brochure', 'video'])
 * @returns {array} - Processed data with parsed file lists
 */
export const processApiDataWithFileLists = (apiData, fileFields = ['thumbnail', 'brochure', 'video']) => {
    if (!Array.isArray(apiData)) return [];

    return apiData.map(item => {
        const processedItem = { ...item };

        fileFields.forEach(field => {
            if (processedItem[field]) {
                processedItem[field] = parseFileListFromString(processedItem[field]);
            }
        });

        return processedItem;
    });
};
  